import { assign, range } from 'lodash';
import React, { useEffect, useState } from 'react';
import { isSafari } from 'react-device-detect';
import { Platform, useWindowDimensions } from 'react-native';
import styled from 'styled-components/native';

import Colors from '../../constants/Colors';
import {
  VictoryArea,
  VictoryAxis,
  VictoryChart,
  VictoryLabel,
  VictoryLine
} from '../charts/Victory';
import Spacer from '../Spacer';
import { B } from '../Typography';

const StyledView = styled.View`
  padding: 2px;
  align-items: center;
  max-height: ${isSafari ? 650 : 400}px;
  min-height: 200px;
`;

const StyledTextView = styled.View`
  align-items: center;
`;

const StyledText = styled(B)`
  font-size: 12px;
  text-transform: uppercase;
`;

/*
  data - Values for the Graph
  xAxisMaxValueLabel - Maximum x-axis domain value
  yAxisMaxValueLabel - Maximum y-axis domain value
*/
export type DataTrackerChartProps = {
  title?: string;
  yAxisLeftLabel?: string;
  yAxisRightLabel?: string;
  enableRightAxis?: boolean;
  leftAxisEntityId?: string;
  leftAxisColumnName?: string;
  rightAxisEntityId?: string;
  rightAxisColumnName?: string;
  data: any[];
  xAxisMaxValueLabel: number;
  yAxisMaxValueLabel: number;
  fullWidth?: boolean;
  displayAllDayLabels?: boolean;
};

const DataTrackerChart: React.FC<DataTrackerChartProps> = ({
  title,
  enableRightAxis = false,
  yAxisLeftLabel = 'Measurement',
  yAxisRightLabel = 'Dose',
  data,
  xAxisMaxValueLabel,
  yAxisMaxValueLabel,
  fullWidth,
  displayAllDayLabels
}) => {
  const window = useWindowDimensions();
  const isTablet = window.width >= 740 && window.width <= 1024;
  const isDesktop = window.width >= 1025 && window.width <= 1439;
  const isLargeDesktop = window.width >= 1440;
  const xOffsets = [
    49.5,
    window.width *
      (Platform.OS === 'ios' && isTablet && window.height <= 800
        ? 0.48
        : Platform.OS === 'web' && isTablet && window.height <= 800
          ? 0.48
          : Platform.OS === 'web' && isTablet && window.width <= 800
            ? 0.59
            : isTablet && window.width >= 900
              ? 0.47
              : isTablet
                ? 0.59
                : isDesktop
                  ? 0.37
                  : isLargeDesktop
                    ? 0.2
                    : 0.88) -
      49.5
  ];
  const tickPadding = [-3, -18];
  const anchors = ['end', 'start'];
  const graphColors = [
    Colors.dataTrackerAreaGraphColor,
    Colors.dataTrackerAlternateGuideLineColor
  ];
  const graphGuideLineColors = [
    'white',
    Colors.dataTrackerAlternateGuideLineColor
  ];
  const graphGuideTextColors = [
    Colors.lightText,
    Colors.dataTrackerGuideTextColor
  ];
  const graphGuideTextBackgroundColors = [
    'transparent',
    Colors.dataTrackerGuideLineBGTextColor
  ];
  const [maxDataValue, setMaxDataValue] = useState(0);
  const [lastData2ndGraphXValue, setLastData2ndGraphXValue] = useState(0);
  const [lastData2ndGraphYValue, setLastData2ndGraphYValue] = useState(0);

  useEffect(() => {
    xAxisData(data);
  }, [data]);

  // Retrieve x-axis datas
  const xAxisData = (datas: any) => {
    datas.map((dataValues: { x: number; y: number }[], index: number) => {
      if (index === 0) {
        const newArray = dataValues.map((item: { x: number }) => item.x);
        setMaxDataValue(Math.floor(Math.max(...newArray)));
      }
      if (index === 1) {
        const newXArray = dataValues.map((item: { x: number }) => item.x);
        const newYArray = dataValues.map((item: { y: number }) => item.y);
        setLastData2ndGraphXValue(newXArray[newXArray.length - 1]);
        setLastData2ndGraphYValue(newYArray[newYArray.length - 1]);
      }
    });
  };

  // Y-axis tick values setup
  const yTickValues = () => {
    const array = [];
    for (const i of range(0, yAxisMaxValueLabel + 1)) {
      array.push(i);
    }
    // filter
    return array.filter(function (a) {
      return a % 4 === 0;
    });
  };

  // Y-axis data Line Graph computation
  const yDataLineGraphManipulation = (data: any, index: number) => {
    const newArray = data.map(
      (item: { x: number; y: number; label?: string }) => ({
        x: item.x,
        y: item.y / maxima[index],
        label: item.label
      })
    );
    return newArray;
  };

  // Dynamic position of x-axis Victory line label
  const xAxisLabelPosition = () => {
    if (isLargeDesktop) {
      return manipulateXAxisLabel(2);
    } else if (isDesktop) {
      if (Platform.OS === 'web') {
        return manipulateXAxisLabel(5);
      } else {
        return manipulateXAxisLabel(5);
      }
    } else {
      if (Platform.OS === 'web') {
        if (
          lastData2ndGraphYValue === 6 ||
          lastData2ndGraphYValue === 11 ||
          lastData2ndGraphYValue === 12
        ) {
          return manipulateXAxisLabel(11);
        }
        return manipulateXAxisLabel(9);
      } else {
        if (window.width <= 375) {
          return manipulateXAxisLabel(15);
        } else {
          if (
            lastData2ndGraphYValue === 6 ||
            lastData2ndGraphYValue === 11 ||
            lastData2ndGraphYValue === 12
          ) {
            return manipulateXAxisLabel(15);
          }
          return manipulateXAxisLabel(9);
        }
      }
    }
  };

  // Computation of x-axis Victory line label
  const manipulateXAxisLabel = (repositionXAxisValue: number) => {
    if (lastData2ndGraphXValue > xAxisMaxValueLabel - repositionXAxisValue) {
      return 33;
    } else {
      return 32;
    }
  };

  // Dynamic position of y-axis Victory line label
  const yAxisLabelPosition = () => {
    if (isLargeDesktop) {
      return setupYAxisLabelPosition(2);
    } else if (isDesktop) {
      if (Platform.OS === 'web') {
        return setupYAxisLabelPosition(5);
      } else {
        return setupYAxisLabelPosition(6);
      }
    } else {
      if (Platform.OS === 'web') {
        if (
          lastData2ndGraphYValue === 6 ||
          lastData2ndGraphYValue === 7 ||
          lastData2ndGraphYValue === 11 ||
          lastData2ndGraphYValue === 12
        ) {
          return setupYAxisLabelPosition(11);
        } else {
          return setupYAxisLabelPosition(9);
        }
      } else {
        if (window.width < 375) {
          return setupYAxisLabelPosition(21);
        } else if (window.width == 375) {
          return setupYAxisLabelPosition(17);
        } else {
          if (
            lastData2ndGraphYValue === 6 ||
            lastData2ndGraphYValue === 7 ||
            lastData2ndGraphYValue === 11 ||
            lastData2ndGraphYValue === 12
          ) {
            return setupYAxisLabelPosition(21);
          } else {
            return setupYAxisLabelPosition(9);
          }
        }
      }
    }
  };

  // Computation of y-axis Victory line label
  const setupYAxisLabelPosition = (repositionYAxisValue: number) => {
    if (lastData2ndGraphXValue > xAxisMaxValueLabel - repositionYAxisValue) {
      return manipulateYAxisLabelPosition();
    } else {
      return manipulateYAxisLabelEndPosition();
    }
  };

  const manipulateYAxisLabelEndPosition = () => {
    if (lastData2ndGraphYValue === 0) {
      return -10;
    } else if (lastData2ndGraphYValue === (yAxisMaxValueLabel / 4) * 5) {
      return 15;
    } else {
      return 2;
    }
  };

  const manipulateYAxisLabelPosition = () => {
    if (
      (lastData2ndGraphYValue >= 2 && lastData2ndGraphYValue <= 5) ||
      (lastData2ndGraphYValue >= 10 && lastData2ndGraphYValue <= 10)
    ) {
      return 2;
    } else if (lastData2ndGraphYValue >= 6 && lastData2ndGraphYValue <= 6) {
      return -9;
    } else if (
      ((yAxisMaxValueLabel / 4) * 5 - 1 >= lastData2ndGraphYValue &&
        (yAxisMaxValueLabel / 4) * 5 - 1 <= lastData2ndGraphYValue) ||
      (yAxisMaxValueLabel / 4) * 5 === lastData2ndGraphYValue
    ) {
      return 14;
    } else if (lastData2ndGraphYValue >= 12 && lastData2ndGraphYValue <= 12) {
      return -8;
    } else if (lastData2ndGraphYValue >= 11 && lastData2ndGraphYValue <= 11) {
      return -21;
    } else {
      return -10;
    }
  };

  const horizontalGuideLines = [
    [
      { x: 1, y: 9 },
      { x: xAxisMaxValueLabel, y: 9, label: 'Good' }
    ],
    [
      { x: 1, y: 5 },
      { x: xAxisMaxValueLabel, y: 5, label: 'Normal' }
    ]
  ];

  const getChartElementStyles = (index: number) => {
    const xAxisTickStyle = {
      ticks: { padding: -2 },
      tickLabels: {
        fill: (tick: any) =>
          tick.index < maxDataValue
            ? Colors.darkText
            : Colors.dataTrackerTickDisabled,
        fontSize: '10px',
        fontFamily: 'WorkSans-SemiBold',
        lineHeight: '12px',
        letterSpacing: '-0.49px'
      }
    };

    const yAxisTickStyle = {
      ticks: { padding: tickPadding[index] },
      tickLabels: {
        fill: Colors.darkText,
        fontSize: '10px',
        fontFamily: 'WorkSans-SemiBold',
        lineHeight: '83.85px',
        letterSpacing: '-0.49px',
        textAnchor: anchors[index]
      }
    };

    const xAxisStyle = assign({}, xAxisTickStyle, {
      grid: {
        stroke: index === 0 ? 'rgba(239, 242, 243, 0.3)' : 'transparent'
      },
      axis: { stroke: 'rgba(239, 242, 243, 0.3)' },
      axisLabel: {
        fill: Colors.darkText,
        fontSize: '10px',
        fontFamily: 'WorkSans-SemiBold',
        lineHeight: '83.85px',
        letterSpacing: '-0.49px',
        padding: 42
      }
    });

    const xAxisDummyLineStyle = {
      axis: { stroke: 'rgba(239, 242, 243, 0.3)' },
      ticks: { padding: -10.5 },
      tickLabels: {
        fill: Colors.darkText,
        fontSize: '27px',
        fontFamily: 'WorkSans-SemiBold'
      },
      grid: { stroke: 'transparent' }
    };

    const yAxisStyle = assign({}, yAxisTickStyle, {
      grid: { stroke: Colors.lightText, strokeWidth: 0 },
      axis: { stroke: 'rgba(239, 242, 243, 0.3)' },
      axisLabel: {
        fill: Colors.darkText,
        fontSize: '9px',
        fontFamily: 'WorkSans-SemiBold',
        lineHeight: '12px',
        letterSpacing: '-0.49px',
        padding: index === 0 ? 30 : -40
      }
    });

    const labelStyle = {
      fill: index === -1 ? Colors.darkText : graphGuideTextColors[index],
      fontSize: '10px',
      fontFamily: 'WorkSans-SemiBold',
      letterSpacing: '-0.49px'
    };

    const guideLineStyle = {
      data: {
        stroke: graphGuideLineColors[index],
        strokeWidth: 1,
        strokeDasharray: '3, 1'
      }
    };

    const lineStyle = {
      data: {
        stroke: graphColors[index],
        strokeWidth: 3
      }
    };

    return {
      labels: labelStyle,
      xAxis: xAxisStyle,
      xAxisDummyLine: xAxisDummyLineStyle,
      yAxis: yAxisStyle,
      guideLine: guideLineStyle,
      line: lineStyle
    };
  };

  const maxima = data.map((data: any, index) => {
    if (index === 0) {
      return yAxisMaxValueLabel;
    } else {
      return Math.max(...data.map((data: any) => data.y)) * 2;
    }
  });
  return (
    <StyledView>
      {title ? (
        <StyledTextView>
          <StyledText color='dark'>{title}</StyledText>
        </StyledTextView>
      ) : (
        <Spacer size={2} />
      )}
      <VictoryChart
        padding={{ top: 12, bottom: 60, left: 50, right: 50 }}
        width={
          window.width *
          (fullWidth
            ? 0.88
            : isTablet && window.height <= 800
              ? 0.48
              : isTablet && window.width >= 900
                ? 0.47
                : isTablet
                  ? 0.59
                  : isDesktop
                    ? 0.37
                    : isLargeDesktop
                      ? 0.2
                      : 0.88)
        }
        height={
          Platform.OS === 'web' && isTablet
            ? 150
            : Platform.OS === 'web' && isDesktop
              ? 140
              : Platform.OS === 'web' && isLargeDesktop && isSafari
                ? 145
                : Platform.OS === 'web' && isLargeDesktop
                  ? 140
                  : isTablet
                    ? 254
                    : isDesktop
                      ? 258
                      : isLargeDesktop
                        ? 140
                        : 188
        }
        domain={{ y: [0, 1] }}
      >
        {/* Y-Axis */}
        {data.map((_data: any, index) =>
          index === 0 ? (
            <VictoryAxis
              crossAxis={false}
              label={yAxisLeftLabel.toUpperCase()}
              dependentAxis
              key={index}
              offsetX={xOffsets[index]}
              style={getChartElementStyles(index).yAxis}
              tickValues={[0, 0.25, 0.5, 0.75, 1]}
              fixLabelOverlap
              tickFormat={
                _data.length === 0
                  ? () => ''
                  : (tickValue: number) => Math.round(tickValue * maxima[index])
              }
            />
          ) : (
            enableRightAxis && (
              <VictoryAxis
                crossAxis={false}
                label={yAxisRightLabel.toUpperCase()}
                dependentAxis
                key={index}
                offsetX={xOffsets[index]}
                style={getChartElementStyles(index).yAxis}
                tickValues={[0, 0.25, 0.5, 0.75, 1]}
                tickFormat={
                  _data.length === 0
                    ? () => ''
                    : (tickValue: number) =>
                        Math.round(tickValue * maxima[index])
                }
              />
            )
          )
        )}
        {/* X-Axis */}
        <VictoryAxis
          key={'x-axis'}
          label='DAY'
          offsetY={59.2}
          style={getChartElementStyles(0).xAxis}
          domain={{ x: [1, xAxisMaxValueLabel] }}
          tickCount={xAxisMaxValueLabel}
          fixLabelOverlap={displayAllDayLabels ? false : true}
        />

        {/* Present Day Underline */}
        <VictoryAxis
          key={'underline'}
          offsetY={59.2}
          style={getChartElementStyles(0).xAxisDummyLine}
          domain={{ x: [1, xAxisMaxValueLabel] }}
          tickCount={xAxisMaxValueLabel}
          tickFormat={(tickLabel: number) =>
            tickLabel === maxDataValue ? `_` : ``
          }
        />

        {/* Y-Axis */}
        {/* {data.map((_data: any, index: number) =>
          index === 0 ? (
            <VictoryAxis
              crossAxis={false}
              label='SLEEP DURATION (HRS)'
              dependentAxis
              key={index}
              offsetX={xOffsets[index]}
              style={getChartElementStyles(index).yAxis}
              tickValues={[0, 0.25, 0.5, 0.75, 1]}
              fixLabelOverlap
              tickFormat={(tickValue: number) =>
                Math.round(tickValue * maxima[index])
              }
            />
          ) : (
            <VictoryAxis
              crossAxis={false}
              label='DOSE (MG)'
              dependentAxis
              key={index}
              offsetX={xOffsets[index]}
              style={getChartElementStyles(index).yAxis}
              tickValues={[0, 0.25, 0.5, 0.75, 1]}
              tickFormat={(tickValue: number) =>
                Math.round(tickValue * maxima[index])
              }
            />
          )
        )} */}

        {/* Graph */}
        {data.map((data: any, index: number) =>
          index === 0 ? (
            <VictoryArea
              key={index}
              interpolation='monotoneX'
              style={{ data: { fill: graphColors[index] } }}
              data={yDataLineGraphManipulation(data, index)}
            />
          ) : (
            enableRightAxis && (
              <VictoryLine
                key={index}
                data={yDataLineGraphManipulation(data, index)}
                style={getChartElementStyles(index).line}
                labels={(data: { label: any }) => data.label}
                labelComponent={
                  <VictoryLabel
                    dx={xAxisLabelPosition()}
                    dy={yAxisLabelPosition()}
                    verticalAnchor={'middle'}
                    lineHeight={2}
                    style={getChartElementStyles(-1).labels}
                    backgroundStyle={{
                      fill: Colors.dataTrackerAlternateGuideLineColor,
                      rx: 9
                    }}
                    backgroundPadding={{
                      top: -2.8,
                      bottom: -2.8,
                      left: 8,
                      right: 8
                    }}
                  />
                }
              />
            )
          )
        )}

        {/* Guide Lines */}
        {horizontalGuideLines.map((data, index) => (
          <VictoryLine
            key={index}
            data={data}
            style={getChartElementStyles(index).guideLine}
            labels={(data: { label: any }) => data.label}
            labelComponent={
              <VictoryLabel
                dx={index === 0 ? -15 : -22.5}
                dy={8}
                lineHeight={2}
                style={getChartElementStyles(index).labels}
                backgroundStyle={{
                  fill: graphGuideTextBackgroundColors[index],
                  rx: 9
                }}
                backgroundPadding={{
                  top: -2.8,
                  bottom: -2.8,
                  left: index === 0 ? 8 : 6,
                  right: index === 0 ? 8 : 6
                }}
              />
            }
          />
        ))}
      </VictoryChart>
    </StyledView>
  );
};

export default DataTrackerChart;
